import { useEffect, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState, InlineWarning, KpiCard, ListingLinkButton, Panel } from "@/components/common/Primitives";
import { ValuationDiagnostics } from "@/components/market/ValuationDiagnostics";
import { api, apiError } from "@/lib/api";
import { countryName, eur, eurSigned, km, num, pct } from "@/lib/format";

export default function MarketData() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/market-data")
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, []);

  return (
    <AppShell
      title="Marktdata"
      subtitle="Liquiditeit en prijsspreiding op basis van werkelijk opgeslagen advertenties"
      freshness={[{ label: "Gegenereerd", value: data?.generated_at }]}
    >
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <KpiCard label="NL vergelijkingsauto's" value={num(data?.totals?.comparables ?? 0)} testid="market-comparables" />
        <KpiCard label="Bronadvertenties" value={num(data?.totals?.source_listings ?? 0)} testid="market-sources" />
        <KpiCard label="Geanalyseerd" value={num(data?.totals?.analysed ?? 0)} testid="market-analysed" />
        <KpiCard label="Prijsverlagingen" value={num(data?.totals?.price_reductions ?? 0)} testid="market-reductions" />
      </div>

      <div className="mt-3">
        <InlineWarning tone="info">{data?.basis}</InlineWarning>
      </div>

      <div className="mt-3 grid gap-2">
        <ValuationDiagnostics />

        <Panel title="Marktsegmenten (NL)" testid="market-segments-panel">
          {(data?.segments || []).length === 0 ? (
            <EmptyState
              title="Geen Nederlandse marktdata opgeslagen"
              description="Marktstatistieken worden berekend uit opgeslagen NL-advertenties. Zodra de AutoScout24 NL-koppeling actief is (of echte vergelijkingsauto's zijn ingevoerd) verschijnen hier mediaanprijzen, spreiding en liquiditeit."
              testid="market-segments-empty"
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th>Aantal</th>
                    <th>Mediaanprijs</th>
                    <th>P25</th>
                    <th>P75</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Spreiding</th>
                    <th>Mediaan KM</th>
                    <th>Bouwjaren</th>
                    <th>Liquiditeit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.segments.map((s) => (
                    <tr key={s.segment} data-testid={`segment-${s.segment}`}>
                      <td className="text-[12.5px] font-semibold">{s.segment}</td>
                      <td className="num">{num(s.count)}</td>
                      <td className="num font-semibold">{eur(s.median_price)}</td>
                      <td className="num">{eur(s.p25)}</td>
                      <td className="num">{eur(s.p75)}</td>
                      <td className="num">{eur(s.min_price)}</td>
                      <td className="num">{eur(s.max_price)}</td>
                      <td className="num">{pct(s.dispersion_pct, { fallback: "—" })}</td>
                      <td className="num">{km(s.median_mileage)}</td>
                      <td className="num">{s.year_range ? s.year_range.join("–") : "—"}</td>
                      <td className="text-[12px]">{s.liquidity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Prijsverlagingen" subtitle="Vastgesteld via opgeslagen listing snapshots" testid="market-price-drops">
          {(data?.price_reductions || []).length === 0 ? (
            <p className="px-3 py-4 text-[12px] text-slate-500">
              Nog geen prijsverlagingen vastgesteld. Er zijn minimaal twee observaties van dezelfde
              advertentie nodig.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Voertuig</th>
                    <th>Land</th>
                    <th>Vorige prijs</th>
                    <th>Huidige prijs</th>
                    <th>Verschil</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.price_reductions.map((l) => (
                    <tr key={l.id}>
                      <td className="text-[12.5px] font-semibold">
                        {[l.make, l.model, l.variant].filter(Boolean).join(" ")}
                      </td>
                      <td className="text-[12px]">{countryName(l.country)}</td>
                      <td className="num">{eur(l.previous_price)}</td>
                      <td className="num font-semibold">{eur(l.price)}</td>
                      <td className="num font-semibold text-emerald-700">{eurSigned(l.price_change)}</td>
                      <td>
                        <ListingLinkButton url={l.url} testid={`drop-url-${l.id}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
